// =============================================================================
// PRODUCT THUMB NAV — Horizon `Component` port of the PDP gallery thumbnail up/down
// arrows driver (src/js/objects/global/product-gallery-thumbnav.js). Wave-2 atomic
// driver-swap: ZERO behaviour/visual change — the `<product-thumb-nav>` host wraps the
// two `.product__thumb-nav` buttons in sections/main-product.liquid (thumbs_aside
// gallery, Figma node 59:2730) and re-parents the click wiring onto the Component
// lifecycle. Loaded as native ESM via the `@theme/*` importmap
// (snippets/horizon-importmap.liquid) instead of the webpack src/js bundle.
//
// WHY DRIVE THE MAIN SWIPER (not the thumbs sublist directly): Motto's sliderInit
// (assets/global.js) links the sublist as the main swiper's `thumbs`, so advancing the
// MAIN slide auto-scrolls the thumbnail column to keep the active thumb in view (its
// slideChangeTransitionStart calls mediaSublist.swiper.slideTo). Calling
// slidePrev/slideNext on the main swiper therefore moves the hero AND the thumbnail
// strip in one wired motion — no second swiper to keep in sync.
//
// RESOLVE-AT-CLICK IS LOAD-BEARING — do NOT cache the Swiper instance in
// connectedCallback. The vendored Motto stack owns and REPEATEDLY destroy+recreates
// the `.js-media-list` Swiper (destroy(true,true) deletes `el.swiper`; recreation
// reassigns it):
//   (a) initial create — assets/update-slider.js (deferred, emitted from
//       main-product.liquid) runs AFTER this module can connect, so at connect time
//       on first load NO swiper exists yet (init-order race);
//   (b) every shopify:section:load → update-slider.js swiperInit() destroys+recreates
//       document-wide;
//   (c) quick-add modal hide() (assets/quick-add.js) destroys ALL page
//       .js-media-list/.js-media-sublist swipers then sliderInit(true) — closing ANY
//       quickview swaps the PDP instance;
//   (d) variant-change media path (assets/global.js updateProductMediaFromHtml →
//       destroyProductMediaSwipers + reinitProductMediaGalleries);
//   (e) window.initFeaturedProduct (assets/global.js).
// A cached instance would go stale on ALL five; click-time resolution + the
// `!swiper.destroyed` guard (covers mid-destroy windows) absorbs every one. Clicks
// before Motto's sliderInit are silent no-ops — exactly the old pre-init fallback.
//
// LIFECYCLE PARITY: connectedCallback on the host replaces BOTH the old
// DOMContentLoaded boot and the old document-level shopify:section:load + 50ms
// rescan — the editor re-render inserts fresh nodes and the custom element upgrades
// on insertion (same coverage, slightly earlier; safe because resolution is at
// click). The old per-button listeners were never removed (they died with the node);
// here an AbortController tears them down on disconnect and re-arms on reconnect —
// equivalent semantics, no leak on editor re-renders. Known coverage SUPERSET
// (declared latent-bug fix, invisible on today's live config): runtime-injected
// copies (variant-change outerHTML media re-render on combined-listing switches;
// a hypothetical thumbs_aside quickview) go live via connectedCallback where the old
// module left them dead — never the reverse.
//
// DOM/LOOK PARITY: the host carries an inline `display: contents` in the Liquid so it
// adds NO layout box and is NOT a positioned ancestor — the absolute buttons still
// anchor to `.product__media-sublist-outer` (position:relative + overflow:hidden,
// assets/section-main-product.css:733). All markup classes kept verbatim
// (`product__thumb-nav*`, `no-js-hidden`, `focus-inset`); this module toggles ZERO
// classes and writes ZERO styles. The old data-thumb-nav-bound idempotency flag is
// dropped (it guarded document-wide rescans; per-instance binding needs none, and it
// had zero CSS/JS consumers). data-thumb-nav stays in the markup (harmless, zero
// consumers besides the old module).
//
// NO requiredRefs — the old module bound whatever buttons existed and never threw;
// a malformed root stays silently inert (MissingRefError would be a behaviour delta).
//
// UNTRACKED new file (alongside assets/component.js + assets/utilities.js) — re-verify
// it exists before any push; a parallel agent's git op on assets/ can remove it.
// =============================================================================
import { Component } from '@theme/component';

// Resolve the live MAIN swiper that shares this button's media wrapper — verbatim from
// the old module. Returns null until Motto's sliderInit has created it (clicks before
// that are no-ops — the arrows are no-js-hidden anyway). Per-button closest() scoping
// keeps multi-gallery pages isolated.
function mainSwiperFor(button) {
  const wrapper = button.closest('.product__media-list-wrapper');
  const list = wrapper && wrapper.querySelector('.js-media-list');
  return list && list.swiper && !list.swiper.destroyed ? list.swiper : null;
}

/**
 * The PDP thumbnail-column up/down nav (`<product-thumb-nav style="display: contents">`)
 * — drives the vendored Motto MAIN media Swiper's slidePrev/slideNext.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [prev] - the up arrow (button.product__thumb-nav--prev).
 * @property {HTMLButtonElement} [next] - the down arrow (button.product__thumb-nav--next).
 *
 * @extends {Component<Refs>}
 */
export class ProductThumbNav extends Component {
  // Aborts the per-button click listeners on disconnect; re-armed on every (re)connect.
  #clicks = null;

  connectedCallback() {
    super.connectedCallback(); // populates this.refs (synchronous) + wires the base MutationObserver

    this.#clicks?.abort(); // defensive: a double-connect without a disconnect must not double-bind
    this.#clicks = new AbortController();
    const { signal } = this.#clicks;

    // ref name carries the direction — same outcome as the old dataset.thumbNav check
    // ('prev' → slidePrev, anything else → slideNext; markup only emits prev/next).
    const { prev, next } = this.refs;
    if (prev) prev.addEventListener('click', (event) => this.#onClick(event, prev, 'prev'), { signal });
    if (next) next.addEventListener('click', (event) => this.#onClick(event, next, 'next'), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback(); // the base class disconnects its MutationObserver here
    this.#clicks?.abort();
    this.#clicks = null;
  }

  /**
   * Exact old click handler: UNCONDITIONAL preventDefault (inert on type='button' —
   * kept for parity), swiper resolved AT CLICK TIME, silent no-op when absent/destroyed.
   *
   * @param {MouseEvent} event
   * @param {HTMLButtonElement} button
   * @param {'prev' | 'next'} direction
   */
  #onClick(event, button, direction) {
    event.preventDefault();
    const swiper = mainSwiperFor(button);
    if (!swiper) return;
    if (direction === 'prev') {
      swiper.slidePrev();
    } else {
      swiper.slideNext();
    }
  }
}

if (!customElements.get('product-thumb-nav')) {
  customElements.define('product-thumb-nav', ProductThumbNav);
}
